import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const testimonialKeys = ["first", "second"] as const;

export function SocialProofSection() {
  const { t } = useTranslation();

  return (
    <section className="py-12 lg:py-32 bg-background min-h-[300px]">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-8 lg:mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
            {t("socialProof.title")}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-body">
            {t("socialProof.subtitle")}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-6">
          {testimonialKeys.map((key, index) => {
            const isFeatured = index === 0;

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className={`bg-card rounded-2xl shadow-sm border border-border relative ${
                  isFeatured ? "p-6 sm:p-10 lg:p-14" : "p-5 sm:p-8"
                }`}
              >
                {/* Large decorative quote mark */}
                <motion.span
                  className={`block font-heading text-coral/15 leading-none select-none ${
                    isFeatured ? "text-[80px] lg:text-[100px]" : "text-[60px]"
                  }`}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.15, duration: 0.4 }}
                  aria-hidden
                >
                  &ldquo;
                </motion.span>

                <blockquote
                  className={`font-body leading-relaxed italic text-foreground -mt-6 ${
                    isFeatured
                      ? "text-xl lg:text-2xl"
                      : "text-base lg:text-lg"
                  }`}
                >
                  {t(`socialProof.testimonials.${key}.quote`)}
                </blockquote>

                <div className="mt-8 flex items-center gap-4">
                  {/* Author avatar placeholder */}
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary font-subheading">
                      {t(`socialProof.testimonials.${key}.author`)
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm font-subheading">
                      {t(`socialProof.testimonials.${key}.author`)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 font-body">
                      {t(`socialProof.testimonials.${key}.company`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
